<script setup lang="ts">
import { computed, defineProps } from "vue";
import { MonthDayReport } from "@/types/calculators";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  dayReport: MonthDayReport;
  dayIndex: number;
}>();

const { t } = useI18n();

const weekOfDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const starStructureDesc = computed(() => {
  if (props.dayReport.personal_status.structures.length > 0) {
    return props.dayReport.personal_status.structures[0];
  }
  if (props.dayReport.personal_status.stars.length > 0) {
    return props.dayReport.personal_status.stars[0];
  }
  return null;
});
const yiDesc = computed(() => {
  if (props.dayReport.yi_preview.length > 0) {
    return `宜: ${props.dayReport.yi_preview[0].name} (${props.dayReport.yi_preview[0].ua})`;
  }
  return null;
});
</script>

<template>
  <div
    class="p-3.5 sm:p-4 rounded-xl border-2 border-gold/40 bg-[#FFFDF9] hover:border-active hover:shadow-md transition-all cursor-pointer flex flex-col justify-between relative overflow-hidden group"
  >
    <div
      class="absolute top-0 right-0 px-2 py-0.5 text-[10px] font-bold rounded-bl bg-gold text-white"
    >
      #{{ dayIndex + 1 }} • {{ dayReport.combined_index }}%
    </div>
    <div>
      <div class="text-xs text-muted font-medium">
        {{ dayReport.date.split("-").reverse().join(".") }} ({{
          t(`week-names.${weekOfDays[dayReport.day_of_week_num - 1]}`)
        }})
      </div>
      <div class="flex items-baseline gap-2 mt-1">
        <span class="text-xl sm:text-2xl font-bold text-brown font-caudex">
          {{ dayReport.pillars.day }}
        </span>
        <span class="text-xs font-semibold text-active">
          {{ dayReport.officer_12.name }} ({{ dayReport.officer_12.title_ua }})
        </span>
      </div>
      <div class="mt-2 space-y-1">
        <div class="text-[11px] font-bold text-success flex items-center gap-1">
          <span>
            ✓ {{ t("calculators.personal-date.top-days.rang") }}
            {{ dayReport.general_status.rank_code }}
          </span>
          <span>
            • {{ t("calculators.personal-date.top-days.bazi") }}
            {{ dayReport.personal_status.score }}%
          </span>
        </div>
        <div
          v-if="starStructureDesc !== null"
          class="text-[11px] text-[#9A6E1E] bg-[#FFF6E4] px-2 py-0.5 rounded font-medium truncate"
        >
          {{ starStructureDesc }}
        </div>
      </div>
    </div>
    <div class="mt-3 pt-2 border-t border-divider/80">
      <div
        class="text-[11px] text-brown font-medium truncate"
        v-if="yiDesc !== null"
      >
        {{ yiDesc }}
      </div>
      <div
        class="text-[11px] text-active font-bold mt-1.5 flex items-center justify-between group-hover:translate-x-0.5 transition-transform"
      >
        <span>{{ t("calculators.personal-date.top-days.full-report") }}</span>
        <span>→</span>
      </div>
    </div>
  </div>
</template>
